import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import Container from '../components/Container';
import Button from '../components/Button';
import Footer from '../components/Footer';
import CookieLogo from '../assets/cookie.png';

// Import camera screenshots
import loginScreen from '../assets/camerascreenshots/login.png';
import mainScreen from '../assets/camerascreenshots/mainscreen.png';
import onlineTest from '../assets/camerascreenshots/onlinetest.png';

// Top Banner Component
const TopBanner = styled.div`
  width: 100%;
  background: linear-gradient(135deg, rgba(45, 108, 255, 0.9) 0%, rgba(110, 168, 255, 0.8) 100%);
  color: #fff;
  padding: 0.8rem 2rem;
  text-align: center;
  font-size: 0.95rem;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(45, 108, 255, 0.3);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    padding: 0.7rem 1rem;
    font-size: 0.9rem;
  }
`;

const BannerLink = styled.a`
  color: #fff;
  text-decoration: none;
  font-weight: 700;
  transition: all 0.2s ease;
  
  &:hover {
    text-decoration: underline;
    color: #f0f7ff;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0e1633 0%, #1e2a4a 100%);
  animation: ${fadeIn} 0.8s ease-out;
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 2rem 0 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (max-width: 768px) {
    padding: 1rem 0 0.5rem;
  }
`;

const ReferrerHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2rem;
`;

const ReferrerLogo = styled.img`
  width: 180px;
  height: 180px;
  object-fit: contain;
  margin-bottom: 1rem;
  padding: 6px;
  
  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
    margin-bottom: 0.5rem;
  }
`;

const ReferrerName = styled.h1`
  font-size: 2.1rem;
  color: #6ea8ff;
  font-weight: 800;
  margin-bottom: 0.5rem;
  text-shadow: 0 0 12px #2d6cffcc;
`;

const ReferrerChannel = styled.div`
  font-size: 1.1rem;
  color: #b6d0ff;
  margin-bottom: 0.5rem;
`;

const ReferrerDescription = styled.p`
  color: #b6d0ff;
  font-size: 1.1rem;
  max-width: 600px;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const ErrorMessage = styled.div`
  background: rgba(44, 100, 255, 0.12);
  color:rgb(255, 45, 45);
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  text-align: center;
  font-weight: 500;
  box-shadow: 0 0 8px #2d6cff44;
`;

const ReferrerLogosRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2.5rem;
  margin-bottom: 1.2rem;
  
  @media (max-width: 768px) {
    gap: 1rem;
    flex-wrap: wrap;
    padding: 0 1rem;
  }
`;

const BigX = styled.div`
  font-size: 5rem;
  font-weight: 900;
  color: #fff;
  text-shadow: 0 0 32px #fff, 0 0 16px #2d6cffcc, 0 0 8px #fff;
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const ProductHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const ProductTitle = styled.h2`
  font-size: 3rem;
  color: #6ea8ff;
  font-weight: 900;
  margin-bottom: 1rem;
  text-shadow: 0 0 16px #2d6cffcc;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const ProductSubtitle = styled.p`
  font-size: 1.3rem;
  color: #b6d0ff;
  max-width: 800px;
  margin: 0 auto 2rem;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 0 1rem;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
  max-width: 1600px;
  margin-left: auto;
  margin-right: auto;
  justify-content: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FeatureCard = styled.div`
  background: rgba(20, 30, 60, 0.65);
  border-radius: 18px;
  padding: 2rem;
  border: 1.5px solid #2d6cff44;
  backdrop-filter: blur(8px);
  box-shadow: 0 8px 32px 0 rgba(34, 60, 120, 0.18);
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #2d6cff;
    box-shadow: 0 12px 40px 0 rgba(45, 108, 255, 0.3);
    transform: translateY(-5px);
  }
`;

const FeatureTitle = styled.h3`
  color: #fff;
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-align: center;
`;

const FeatureDescription = styled.p`
  color: #b6d0ff;
  line-height: 1.6;
  font-size: 1rem;
`;

const OtherToolsLink = styled(Link)`
  display: inline-block;
  background: rgba(20, 30, 60, 0.65);
  color: #6ea8ff;
  text-decoration: none;
  padding: 0.75rem 2rem;
  border-radius: 12px;
  border: 1px solid #2d6cff44;
  font-weight: 600;
  transition: all 0.2s ease;
  margin-bottom: 2rem;
  
  &:hover {
    background: rgba(45, 108, 255, 0.12);
    border-color: #2d6cff;
    transform: translateY(-2px);
  }
`;

const VideosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const DemoVideo = styled.div`
  background: rgba(20, 30, 60, 0.65);
  border-radius: 18px;
  padding: 1.5rem;
  border: 1.5px solid #2d6cff44;
  backdrop-filter: blur(8px);
  box-shadow: 0 8px 32px 0 rgba(34, 60, 120, 0.18);
  text-align: center;
`;

const VideoContainer = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 1rem;
  background: #1a1a1a;
  aspect-ratio: 16/9;
  display: flex;
  align-items: center;
  justify-content: center;
`;



const ScreenshotsSection = styled.div`
  margin-bottom: 3rem;
`;

const CarouselContainer = styled.div`
  position: relative;
  border-radius: 18px;
  overflow: hidden;
  background: rgba(20, 30, 60, 0.65);
  border: 1.5px solid #2d6cff44;
  backdrop-filter: blur(8px);
  box-shadow: 0 8px 32px 0 rgba(34, 60, 120, 0.18);
`;

const CarouselImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: contain;
  position: ${props => props.index === 0 ? 'relative' : 'absolute'};
  top: ${props => props.index === 0 ? 'auto' : '0'};
  left: ${props => props.index === 0 ? 'auto' : '0'};
  opacity: ${props => props.$active ? '1' : '0'};
  transition: opacity 0.5s ease-in-out;
  background: rgba(20, 30, 60, 0.3);
  
  @media (max-width: 768px) {
    height: 300px;
  }
`;

const CarouselControls = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  z-index: 10;
`;

const CarouselDot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$active ? '#6ea8ff' : 'rgba(255, 255, 255, 0.3)'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #6ea8ff;
    transform: scale(1.2);
  }
`;

// New components for conversion optimization
const HookSection = styled.div`
  text-align: center;
  margin: 3rem 0;
  padding: 3rem 2rem;
  background: linear-gradient(135deg, rgba(45, 108, 255, 0.15) 0%, rgba(110, 168, 255, 0.1) 100%);
  border-radius: 24px;
  border: 2px solid #2d6cff44;
  backdrop-filter: blur(8px);
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    margin: 2rem 0;
    padding: 2rem 1rem;
    border-radius: 16px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, #6ea8ff, transparent);
    animation: shimmer 3s infinite;
  }
  
  @keyframes shimmer {
    0% { left: -100%; }
    100% { left: 100%; }
  }
`;

const HookHeadline = styled.h1`
  font-size: 3.5rem;
  font-weight: 900;
  color: #fff;
  margin-bottom: 1.5rem;
  text-shadow: 0 0 20px #6ea8ff88;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 1rem;
    line-height: 1.2;
  }
`;

const HookSubtext = styled.p`
  font-size: 1.4rem;
  color: #b6d0ff;
  margin-bottom: 2rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
    padding: 0 0.5rem;
  }
`;

const UrgencyBanner = styled.div`
  background: linear-gradient(135deg, #ff4757 0%, #ff6b7a 100%);
  color: #fff;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 700;
  margin: 1.5rem auto;
  display: inline-block;
  box-shadow: 0 4px 20px rgba(255, 71, 87, 0.4);
  animation: pulse 2s infinite;
  
  @media (max-width: 768px) {
    padding: 0.8rem 1.5rem;
    font-size: 0.9rem;
    margin: 1rem auto;
    border-radius: 25px;
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
`;

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin: 2rem 0;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.8rem;
    margin: 1.5rem 0;
    padding: 0 0.5rem;
  }
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: center;
  background: rgba(45, 108, 255, 0.1);
  color: #fff;
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid #2d6cff44;
  
  @media (max-width: 768px) {
    padding: 0.8rem;
    font-size: 0.9rem;
  }
  
  &::before {
    content: '✅';
    margin-right: 0.75rem;
    font-size: 1.2rem;
  }
`;

const CTAButton = styled(Button)`
  background: linear-gradient(135deg, #2d6cff 0%, #6ea8ff 100%) !important;
  color: #fff !important;
  border: none !important;
  padding: 1.2rem 3rem !important;
  font-size: 1.3rem !important;
  font-weight: 700 !important;
  border-radius: 50px !important;
  box-shadow: 0 8px 25px rgba(45, 108, 255, 0.4) !important;
  transition: all 0.3s ease !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  
  @media (max-width: 768px) {
    padding: 1rem 2rem !important;
    font-size: 1rem !important;
    letter-spacing: 0.3px !important;
    border-radius: 30px !important;
  }
  
  &:hover {
    color: #fff !important;
    transform: translateY(-3px) !important;
    box-shadow: 0 12px 35px rgba(45, 108, 255, 0.6) !important;
  }
  
  &:active {
    transform: translateY(-1px) !important;
  }
`;

const TrustIndicators = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3rem;
  margin: 2rem 0;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
    margin: 1.5rem 0;
  }
`;

const TrustItem = styled.div`
  text-align: center;
  color: #b6d0ff;
`;

const TrustNumber = styled.div`
  font-size: 2rem;
  font-weight: 900;
  color: #6ea8ff;
  text-shadow: 0 0 10px #6ea8ff66;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const TrustLabel = styled.div`
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

const TestimonialsSection = styled.div`
  margin: 4rem 0;
  padding: 3rem 2rem;
  background: rgba(20, 30, 60, 0.65);
  border-radius: 24px;
  border: 1px solid #2d6cff44;
  backdrop-filter: blur(8px);
  
  @media (max-width: 768px) {
    margin: 2rem 0;
    padding: 2rem 1rem;
    border-radius: 16px;
  }
`;

const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-top: 1.5rem;
  }
`;

const TestimonialCard = styled.div`
  background: rgba(45, 108, 255, 0.08);
  padding: 2rem;
  border-radius: 16px;
  border: 1px solid #2d6cff22;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const TestimonialText = styled.p`
  color: #fff;
  font-style: italic;
  margin-bottom: 1rem;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 0.8rem;
  }
  
  &::before {
    content: '"';
    font-size: 2rem;
    color: #6ea8ff;
  }
  
  &::after {
    content: '"';
    font-size: 2rem;
    color: #6ea8ff;
  }
`;

const TestimonialAuthor = styled.div`
  color: #6ea8ff;
  font-weight: 600;
  
  &::before {
    content: '— ';
  }
`;

const PlatformIcons = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin: 2rem 0;
  flex-wrap: wrap;
`;

const PlatformIcon = styled.div`
  background: rgba(45, 108, 255, 0.1);
  padding: 1rem 1.5rem;
  border-radius: 12px;
  border: 1px solid #2d6cff44;
  color: #b6d0ff;
  font-weight: 600;
  font-size: 0.9rem;
`;

// Camera plan
const cameraPlans = [
  {
    id: 'camera_lifetime',
    name: 'Camera Pro - Lifetime',
    price: 300,
    features: [
      'Lifetime access to DeservedCam',
      'Virtual webcam functionality',
      'Upload ID photos with camera effects',
      'Real-time camera effect processing',
      'Works with all video calling apps',
      'Zoom, pan, and movement effects',
      'High-quality video output',
      'Easy-to-use interface',
      'Regular software updates',
      'Priority customer support',
      'One-time payment, no recurring fees'
    ],
    featured: true
  }
];

const cryptoOptions = [
  { id: 'ltc', name: 'Litecoin', icon: 'Ł' },
  { id: 'btc', name: 'Bitcoin', icon: '₿' },
  { id: 'eth', name: 'Ethereum', icon: 'Ξ' },
  { id: 'usdttrc20', name: 'USDT (TRC-20)', icon: '₮', note: 'Low fees' },
  { id: 'usdterc20', name: 'USDT (ERC-20)', icon: '₮', note: 'Higher fees' }
];

// Camera screenshots array
const cameraScreenshots = [
  { src: loginScreen, alt: "Simple Login Interface" },
  { src: mainScreen, alt: "Main Application Control Panel" },
  { src: onlineTest, alt: "Live Camera Feed Testing" }
];

function ReferralCameraPage() {
  const { referrer } = useParams();
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('camera_lifetime');
  const [selectedCrypto, setSelectedCrypto] = useState('ltc');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentEstimate, setPaymentEstimate] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch referrer info
    fetch(`/api/referrer/${referrer}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) setReferrerInfo(data.referrer);
        else setError('Referrer not found.');
      })
      .catch(() => setError('Error loading referrer info.'));
  }, [referrer]);

  // Auto-advance carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % cameraScreenshots.length
      );
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  const currentPlan = cameraPlans.find(plan => plan.id === selectedPlan);

  useEffect(() => {
    if (step === 2 && selectedCrypto && currentPlan) {
      // Get real exchange rate estimate from backend (avoid CORS issues)
      const getPaymentEstimate = async () => {
        try {
          const response = await fetch('/api/payments/estimate', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              amount: currentPlan.price,
              currency_from: 'usd',
              currency_to: selectedCrypto
            })
          });
          const data = await response.json();
          if (data.success && data.estimate && data.estimate.estimated_amount) {
            setPaymentEstimate({
              fiat: currentPlan.price,
              crypto: data.estimate.estimated_amount.toFixed(8),
              currency: selectedCrypto
            });
          } else {
            // Fallback to showing just USD if estimate fails
            setPaymentEstimate({
              fiat: currentPlan.price,
              crypto: '---',
              currency: selectedCrypto
            });
          }
        } catch (error) {
          console.error('Error getting estimate:', error);
          // Fallback to showing just USD if estimate fails
          setPaymentEstimate({
            fiat: currentPlan.price,
            crypto: '---',
            currency: selectedCrypto
          });
        }
      };
      getPaymentEstimate();     
    }
  }, [step, selectedCrypto, selectedPlan, currentPlan]);

  const proceedToPayment = () => setStep(2);
  const handleCryptoSelect = (cryptoId) => setSelectedCrypto(cryptoId);

  const handlePurchase = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const res = await fetch('/api/refpayment/camera/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_id: selectedPlan,
          crypto_currency: selectedCrypto,
          referrer: referrer,
          price_usd: currentPlan.price
        })
      });

      const data = await res.json();
      if (data.success) {
        const statusUrl = `/status/${data.payment.payment_id}`;
        navigate(statusUrl);
      } else {
        setError(data.message || 'Payment creation failed');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (error && !referrerInfo) {
    return (
      <PageWrapper>
        <TopBanner>
          Join our channels: <BannerLink href="https://t.me/DeservedGatewayBot" target="_blank" rel="noopener noreferrer">@DeservedGatewayBot</BannerLink>
        </TopBanner>
        <Container>
          <ErrorMessage>{error}</ErrorMessage>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <TopBanner>
        Join our channels: <BannerLink href="https://t.me/DeservedGatewayBot" target="_blank" rel="noopener noreferrer">@DeservedGatewayBot</BannerLink>
      </TopBanner>
      <ContentWrapper>
        <Container>
          {referrerInfo && (
            <ReferrerHeader>
              <ReferrerLogosRow>
                <ReferrerLogo src={CookieLogo} alt="Deserved Cookie Logo"/>
                <BigX>×</BigX>
                {referrerInfo.logo_url && <ReferrerLogo src={referrerInfo.logo_url} alt={referrerInfo.name} />}
              </ReferrerLogosRow>
              <ReferrerName>{referrerInfo.name}</ReferrerName>
              {referrerInfo.channel && <ReferrerChannel>{referrerInfo.channel}</ReferrerChannel>}
              {referrerInfo.description && <ReferrerDescription>{referrerInfo.description}</ReferrerDescription>}
            </ReferrerHeader>
          )}

          <HookSection>
            <UrgencyBanner>
              🔥 Only 7 Lifetime Licenses Left This Month
            </UrgencyBanner>
            <HookHeadline>
              🛑 Get Verified Anywhere – Even Amazon – With Our Undetectable Virtual Webcam Tool
            </HookHeadline>
            <HookSubtext>
                              Stop getting rejected by verification systems. Our advanced virtual webcam creates realistic camera effects that pass even the strictest identity checks.
            </HookSubtext>
            
            <BenefitsList>
                              <BenefitItem>Real-time camera effects</BenefitItem>
              <BenefitItem>Works with any verification app</BenefitItem>
              <BenefitItem>Instant download – no tech skills</BenefitItem>
              <BenefitItem>Beats Amazon, Revolut, banks</BenefitItem>
            </BenefitsList>

            <CTAButton 
              onClick={() => {
                setSelectedPlan('camera_lifetime');
                // Smooth scroll to pricing section
                setTimeout(() => {
                  const pricingSection = document.querySelector('[data-pricing-section]');
                  if (pricingSection) {
                    pricingSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }, 100);
              }}
            >
              🔥 Get Lifetime Access for $300
            </CTAButton>

            <TrustIndicators>
              <TrustItem>
                <TrustNumber>150+</TrustNumber>
                <TrustLabel>Happy Customers</TrustLabel>
              </TrustItem>
              <TrustItem>
                <TrustNumber>99.8%</TrustNumber>
                <TrustLabel>Success Rate</TrustLabel>
              </TrustItem>
              <TrustItem>
                <TrustNumber>24/7</TrustNumber>
                <TrustLabel>Telegram Support</TrustLabel>
              </TrustItem>
            </TrustIndicators>
          </HookSection>

          <ProductHeader>
            <OtherToolsLink to={`/ref/${referrer}`}>
              ← View All Tools
            </OtherToolsLink>
            <ProductTitle>Why Normal Verification Fails</ProductTitle>
            <ProductSubtitle>
                              Modern verification systems use AI to detect static images, inconsistent lighting, and artificial camera feeds. 
              DeservedCam solves this with professional-grade virtual webcam technology that creates indistinguishable video streams.
            </ProductSubtitle>
          </ProductHeader>

          <h3 style={{ color: '#fff', marginBottom: '2rem', fontSize: window.innerWidth <= 768 ? '1.4rem' : '1.8rem', textAlign: 'center' }}>
            See It In Action
          </h3>
          
          <VideosGrid>
            <DemoVideo>
              <h4 style={{ color: '#fff', marginBottom: '1rem', fontSize: '1.3rem' }}>Tool Interface</h4>
              <VideoContainer>
                <video
                  width="100%"
                  height="100%"
                  controls
                  muted
                  playsInline
                  preload="metadata"
                  style={{ borderRadius: '12px', objectFit: 'cover' }}
                >
                  <source src="https://res.cloudinary.com/dvyegujsj/video/upload/v1752742760/CAMERA_bvvnha.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                

              </VideoContainer>
              <p style={{ color: '#b6d0ff', fontSize: '0.9rem', lineHeight: '1.5' }}>
                Camera tool's new UI and features in action.
              </p>
            </DemoVideo>

            <DemoVideo>
              <h4 style={{ color: '#fff', marginBottom: '1rem', fontSize: '1.3rem' }}>Real Amazon Demo</h4>
              <VideoContainer>
                <video
                  width="100%"
                  height="100%"
                  controls
                  muted
                  playsInline
                  preload="metadata"
                  style={{ borderRadius: '12px', objectFit: 'cover' }}
                >
                  <source src="https://res.cloudinary.com/dvyegujsj/video/upload/v1752742799/CAMANDID_oe50z0.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                

              </VideoContainer>
              <p style={{ color: '#b6d0ff', fontSize: '0.9rem', lineHeight: '1.5' }}>
                Live Amazon verification using our ID bot. <br />
                <span style={{ color: '#6ea8ff', fontWeight: '600' }}>ID bot coming soon!</span>
              </p>
            </DemoVideo>
          </VideosGrid>

          <ScreenshotsSection>
            <h3 style={{ color: '#fff', marginBottom: '2rem', fontSize: window.innerWidth <= 768 ? '1.4rem' : '1.8rem', textAlign: 'center' }}>
              Application Screenshots
            </h3>
            <CarouselContainer>
              {cameraScreenshots.map((screenshot, index) => (
                <CarouselImage
                  key={index}
                  index={index}
                  src={screenshot.src}
                  alt={screenshot.alt}
                  $active={index === currentImageIndex}
                />
              ))}
              <CarouselControls>
                {cameraScreenshots.map((_, index) => (
                  <CarouselDot
                    key={index}
                    $active={index === currentImageIndex}
                    onClick={() => goToImage(index)}
                  />
                ))}
              </CarouselControls>
            </CarouselContainer>
          </ScreenshotsSection>

          <FeaturesGrid>


            <FeatureCard>
              <FeatureTitle>Virtual Webcam Driver</FeatureTitle>
              <FeatureDescription>
                Professional-grade virtual webcam that appears as a real camera to all applications. 
                Works with verification sites, video calls, and streaming platforms.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
                                    <FeatureTitle>Natural Camera Effects</FeatureTitle>
                      <FeatureDescription>
                        Advanced algorithms create authentic camera behavior including zoom effects, 
                                        pan motions, and natural camera behavior that bypass AI detection systems.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureTitle>Instant Setup</FeatureTitle>
              <FeatureDescription>
                One-click installation and setup. Simply upload your photo, select your camera, 
                and start using it immediately with any verification website or app.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureTitle>Universal Compatibility</FeatureTitle>
              <FeatureDescription>
                Works with all major platforms including identity verification sites, 
                video calling apps, streaming software, and browser-based applications.
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureTitle>Undetectable Technology</FeatureTitle>
              <FeatureDescription>
                State-of-the-art rendering technology that creates pixel-perfect video streams 
                indistinguishable from real webcam footage, even under close inspection.
              </FeatureDescription>
            </FeatureCard>
          </FeaturesGrid>

          <div style={{ textAlign: 'center', margin: '3rem 0' }}>
            <h3 style={{ color: '#fff', fontSize: window.innerWidth <= 768 ? '1.4rem' : '1.8rem', marginBottom: '2rem' }}>
              Works With All Major Platforms
            </h3>
            <PlatformIcons>
              <PlatformIcon>🏦 Amazon Verification</PlatformIcon>
              <PlatformIcon>💳 Revolut Identity</PlatformIcon>
              <PlatformIcon>🏛️ Banking KYC</PlatformIcon>
              <PlatformIcon>📹 Zoom Calls</PlatformIcon>
              <PlatformIcon>🎥 Any Video App</PlatformIcon>
              <PlatformIcon>🔒 Government Sites</PlatformIcon>
            </PlatformIcons>
          </div>

          <TestimonialsSection>
            <h3 style={{ color: '#fff', fontSize: window.innerWidth <= 768 ? '1.5rem' : '2rem', textAlign: 'center', marginBottom: '1rem' }}>
              Real Results From Real Customers
            </h3>
            <p style={{ color: '#b6d0ff', textAlign: 'center', marginBottom: '2rem' }}>
              Join our growing community of successfully verified users
            </p>
            
            <TestimonialGrid>
              <TestimonialCard>
                <TestimonialText>
                  The updated camera looks way better now bro, I like it a lot! File size is like 1000x smaller too lol
                </TestimonialText>
                <TestimonialAuthor>@BasicRio</TestimonialAuthor>
              </TestimonialCard>

              <TestimonialCard>
                <TestimonialText>
                  I just did a .es ID, first time, no problems
                </TestimonialText>
                <TestimonialAuthor>@Slam</TestimonialAuthor>
              </TestimonialCard>

              <TestimonialCard>
                <TestimonialText>
                  Frist try, gg, you are the goat man
                </TestimonialText>
                <TestimonialAuthor>@SonicRef7</TestimonialAuthor>
              </TestimonialCard>
            </TestimonialGrid>

            <div style={{ textAlign: 'center', marginTop: '3rem' }}>
              <CTAButton 
                onClick={() => {
                  setSelectedPlan('camera_lifetime');
                  // Smooth scroll to pricing section
                  setTimeout(() => {
                    const pricingSection = document.querySelector('[data-pricing-section]');
                    if (pricingSection) {
                      pricingSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }, 100);
                }}
              >
                Join 150+ Verified Users Today
              </CTAButton>
            </div>
          </TestimonialsSection>
          
          {step === 1 ? (
            <div data-pricing-section>
              <div style={{ textAlign: 'center', margin: '4rem 0 3rem' }}>
                <h2 style={{ color: '#fff', fontSize: window.innerWidth <= 768 ? '1.8rem' : '2.5rem', fontWeight: '900', marginBottom: '1rem' }}>
                  🔥 Limited Time: Lifetime Access Only $300
                </h2>
                <p style={{ color: '#ff4757', fontSize: window.innerWidth <= 768 ? '1rem' : '1.2rem', fontWeight: '600', marginBottom: '2rem' }}>
                  ⚠️ Price increases to $500 next week - Secure your license now!
                </p>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '2rem',
                  marginBottom: '1rem',
                  maxWidth: 600,
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  width: '100%'
                }}
              >
                {cameraPlans.map(plan => (
                  <div
                    key={plan.id}
                    style={{
                      background: 'linear-gradient(135deg, rgba(45, 108, 255, 0.15) 0%, rgba(110, 168, 255, 0.1) 100%)',
                      borderRadius: '24px',
                      padding: '2rem 1.5rem',
                      boxShadow: '0 20px 60px 0 rgba(45, 108, 255, 0.4)',
                      border: '2px solid #6ea8ff',
                      backdropFilter: 'blur(12px)',
                      position: 'relative',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      width: '100%',
                      maxWidth: '500px',
                      height: 'auto',
                      flex: 1
                    }}
                  >
                                         <div style={{ position: 'absolute', top: '-15px', left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #ff4757 0%, #ff6b7a 100%)', color: '#fff', padding: '0.6rem 1.5rem', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 'bold', boxShadow: '0 6px 20px rgba(255, 71, 87, 0.4)', whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
                       🚀 BEST VALUE - SAVE $200
                     </div>
                    
                    <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
                      <h3 style={{ fontSize: '1.8rem', fontWeight: '900', marginBottom: '1rem', color: '#fff', textShadow: '0 0 12px #6ea8ff88' }}>
                        {plan.name}
                      </h3>
                      
                      <div style={{ marginBottom: '1.5rem' }}>
                        <div style={{ fontSize: '1.1rem', color: '#ff4757', textDecoration: 'line-through', marginBottom: '0.5rem' }}>
                          $500 USD
                        </div>
                        <div style={{ fontSize: '3rem', fontWeight: '900', color: '#6ea8ff', textShadow: '0 0 16px #6ea8ff88', lineHeight: '1' }}>
                          ${plan.price}
                        </div>
                        <div style={{ fontSize: '0.95rem', color: '#b6d0ff', marginTop: '0.5rem' }}>
                          One-time payment • No recurring fees
                        </div>
                      </div>
                      
                      <div style={{ background: 'rgba(45, 108, 255, 0.1)', borderRadius: '16px', padding: '1.5rem', marginBottom: '1.5rem' }}>
                        <h4 style={{ color: '#6ea8ff', marginBottom: '0.8rem', fontSize: '1.2rem' }}>✨ Everything Included:</h4>
                        <ul style={{ listStyle: 'none', padding: 0, color: '#b6d0ff', textAlign: 'left' }}>
                          {plan.features.slice(0, 8).map((f, i) => (
                            <li key={i} style={{ marginBottom: '0.6rem', paddingLeft: '1.5rem', position: 'relative', fontSize: '0.95rem' }}>
                              <span style={{ position: 'absolute', left: 0, color: '#2d6cff', fontWeight: 'bold', fontSize: '1.1rem' }}>✓</span> {f}
                            </li>
                          ))}
                          {plan.features.length > 8 && (
                            <li style={{ color: '#6ea8ff', fontWeight: 600, fontSize: '1rem', textAlign: 'center', marginTop: '1rem' }}>
                              + {plan.features.length - 8} more premium features
                            </li>
                          )}
                        </ul>
                      </div>

                      <CTAButton
                        onClick={() => proceedToPayment()}
                        style={{ width: '100%', fontSize: '1.2rem', padding: '1.2rem 1.5rem' }}
                      >
                        🔥 Secure Your Lifetime License Now
                      </CTAButton>
                      
                      <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#b6d0ff', lineHeight: '1.4' }}>
                        ⚡ Instant download after payment<br/>
                        🛡️ 24/7 Telegram support included<br/>
                        💎 Join our growing community
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div style={{marginTop:'2rem',marginBottom:'2rem',background:'rgba(20,30,60,0.65)',borderRadius:'18px',padding: window.innerWidth <= 768 ? '1rem' : '1.5rem',boxShadow:'0 8px 32px 0 rgba(34, 60, 120, 0.18)',border:'1.5px solid #2d6cff44',backdropFilter:'blur(8px)'}}>
                <h3 style={{fontSize: window.innerWidth <= 768 ? '1rem' : '1.2rem',marginBottom:'1rem',textAlign:'center',color:'#fff',textShadow:'0 0 8px #2d6cffcc'}}>Select Payment Method</h3>
                
                {/* Main Cryptocurrencies Row */}
                <div style={{display:'grid',gridTemplateColumns: window.innerWidth <= 768 ? 'repeat(3, 1fr)' : 'repeat(3, 1fr)',gap: window.innerWidth <= 768 ? '0.8rem' : '1rem',marginTop:'1rem',justifyContent:'center'}}>
                  {cryptoOptions.filter(crypto => ['ltc', 'btc', 'eth'].includes(crypto.id)).map(crypto => (
                    <div key={crypto.id} style={{padding: window.innerWidth <= 768 ? '0.6rem' : '0.75rem',border:`2px solid ${selectedCrypto === crypto.id ? '#2d6cff' : '#2d6cff44'}`,borderRadius:'8px',textAlign:'center',cursor:'pointer',transition:'all 0.2s ease',background:selectedCrypto === crypto.id ? 'rgba(44, 108, 255, 0.12)' : 'rgba(20, 30, 60, 0.65)',color:'#b6d0ff',boxShadow:selectedCrypto === crypto.id ? '0 0 12px #2d6cff88' : 'none'}} onClick={() => handleCryptoSelect(crypto.id)}>
                      <div style={{fontSize: window.innerWidth <= 768 ? '1.2rem' : '1.5rem',marginBottom:'0.5rem',color:'#2d6cff',textShadow:'0 0 8px #2d6cffcc'}}>{crypto.icon}</div>
                      <div style={{fontSize: window.innerWidth <= 768 ? '0.7rem' : '0.8rem',fontWeight:selectedCrypto === crypto.id ? 'bold' : 'normal',color:'#b6d0ff'}}>{crypto.name}</div>
                      {crypto.note && <div style={{fontSize: '0.6rem', color: '#b6d0ff', marginTop: '0.3rem'}}>{crypto.note}</div>}
                    </div>
                  ))}
                </div>

                {/* USDT Options Row - Centered */}
                <div style={{display:'flex',justifyContent:'center',gap: window.innerWidth <= 768 ? '0.8rem' : '1rem',marginTop:'1rem',flexWrap:'wrap'}}>
                  {cryptoOptions.filter(crypto => crypto.id.includes('usdt')).map(crypto => (
                    <div key={crypto.id} style={{padding: window.innerWidth <= 768 ? '0.6rem' : '0.75rem',border:`2px solid ${selectedCrypto === crypto.id ? '#2d6cff' : '#2d6cff44'}`,borderRadius:'8px',textAlign:'center',cursor:'pointer',transition:'all 0.2s ease',background:selectedCrypto === crypto.id ? 'rgba(44, 108, 255, 0.12)' : 'rgba(20, 30, 60, 0.65)',color:'#b6d0ff',boxShadow:selectedCrypto === crypto.id ? '0 0 12px #2d6cff88' : 'none',minWidth: window.innerWidth <= 768 ? '100px' : '120px'}} onClick={() => handleCryptoSelect(crypto.id)}>
                      <div style={{fontSize: window.innerWidth <= 768 ? '1.2rem' : '1.5rem',marginBottom:'0.5rem',color:'#2d6cff',textShadow:'0 0 8px #2d6cffcc'}}>{crypto.icon}</div>
                      <div style={{fontSize: window.innerWidth <= 768 ? '0.7rem' : '0.8rem',fontWeight:selectedCrypto === crypto.id ? 'bold' : 'normal',color:'#b6d0ff'}}>{crypto.name}</div>
                      {crypto.note && <div style={{fontSize: '0.6rem', color: '#b6d0ff', marginTop: '0.3rem'}}>{crypto.note}</div>}
                    </div>
                  ))}
                </div>
              </div>
              {paymentEstimate && (
                <div style={{textAlign:'center',marginBottom:'2rem',background:'rgba(20,30,60,0.65)',borderRadius:'18px',padding: window.innerWidth <= 768 ? '1rem' : '1.5rem',boxShadow:'0 8px 32px 0 rgba(34, 60, 120, 0.18)',border:'1.5px solid #2d6cff44',backdropFilter:'blur(8px)'}}>
                  <h4 style={{color:'#fff',marginBottom:'1rem',textShadow:'0 0 8px #2d6cffcc',fontSize: window.innerWidth <= 768 ? '1rem' : '1.2rem'}}>Payment Summary</h4>
                  <p style={{color:'#b6d0ff',marginBottom:'0.5rem',fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem'}}>Plan: <strong style={{color:'#6ea8ff'}}>{currentPlan?.name}</strong></p>
                  <p style={{color:'#b6d0ff',marginBottom:'0.5rem',fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem'}}>Amount: <strong style={{color:'#6ea8ff'}}>{paymentEstimate.crypto} {paymentEstimate.currency.toUpperCase()}</strong></p>
                  <p style={{color:'#b6d0ff',fontSize: window.innerWidth <= 768 ? '0.8rem' : '0.9rem'}}>≈ ${paymentEstimate.fiat} USD</p>
                </div>
              )}
              <div style={{display:'flex',justifyContent:'center',gap:'1rem',flexWrap:'wrap'}}>
                <Button onClick={() => setStep(1)} style={{minWidth:'120px'}}>Back</Button>
                <Button primary onClick={handlePurchase} disabled={loading} style={{minWidth:'120px'}}>
                  {loading ? 'Processing...' : 'Purchase Now'}
                </Button>
              </div>
            </>
          )}
        </Container>
      </ContentWrapper>
      <Footer />
    </PageWrapper>
  );
}

export default ReferralCameraPage; 